<template>

	<div class="page main-container" :class="{ 'is-editing':isEditing, 'is-viewing-diff':(!!diffContent) }" :data-page-guid="guid">
		<page-sidebar
			:guid="guid"
			:lastModUser="lastModUser"
			:lastModDate="lastModDate"
			:isEditing="isEditing"
			:isHomepage="isHomepage"
			:isPrivate="isPrivate"
			:isDeleted="isDeleted"
			:selectedRevision="selectedRevision"
			:latestKnownRevision="latestKnownRevision"
			@toggle-edit="toggleEdit()"
			@cancel-edit="cancelEdit()"
			@save-edit="saveEdit()"
			@make-page-private="makePagePrivate"
			@make-page-public="makePagePublic"
			@selected-revision="handleSelectedRevision"
			@delete-page="handleDeletePage"
			@restore-revision="handleRestoreRevision"
		/>

		<!-- <figure class="flamingo"></figure> -->

		<div class="viewing-revision" v-if="selectedRevision > 0">
			Viewing revision {{ selectedRevision }}
		</div>

		<article>
			<h1 class="title" ref="title" :contenteditable="isEditing">{{ title }}</h1>
			<div class="last-mod-date">
				{{ lastModDateString }}
				<span v-if="lastModUser && lastModUser.fullName" class="inline-last-mod">by {{ lastModUser.fullName }}</span>
				</div>
			<!-- <page-tags v-if="tags.length || isEditing" :pageTags="tags" :pageGUID="guid" :isEditing="isEditing" @tags-changed="tagsChanged" /> -->

			<desktop-menu-bar :editor="editor" :isEditing="isEditing" :guid="guid" />

			<div class="diff-content" v-if="diffContent" v-html="diffContent" />
			
			<editor-content class="editor__content" :editor="editor" />

			<login-popover v-if="isForbidden" ref="loginToView" reason="You need to be logged in to view this page." :isError="true" :prevRoute="prevRoute" />

			<popover ref="pageNotFoundPopover" okButtonName="Go to homepage" :isError="true" @clicked-ok="clickedGotoHomepage" className="page-not-found-popover">
				Page not found.
			</popover>

		</article>
	</div>

</template>


<script>

	import PagesService from '@/services/PagesService'
	import PageSidebar from '@/components/PageSidebar.vue'
	import LoginPopover from '@/components/LoginPopover.vue'
	import Popover from '@/components/Popover.vue'
	import DesktopMenuBar from '@/components/editor/DesktopMenuBar.vue'
	import { Editor, EditorContent } from 'tiptap'
	import {
		Blockquote, BulletList, HardBreak, Heading, HorizontalRule, Table, TableHeader, TableCell, TableRow,
		Image, ListItem, OrderedList, Bold, Code, Italic, Link, Strike, Underline, History
	} from 'tiptap-extensions'

	import iconTable from '../assets/img/icons/table.svg'
	import iconLink from '../assets/img/icons/link.svg'

	const icons = {
		table: iconTable,
		link: iconLink
	}

	const dateformat = require('dateformat')

	export default {
		name: 'page',
		data() {
			return {
				guid: '',
				title: '',
				lastModUser: {},
				lastModDate: (new Date()),
				tags: [],
				isEditing: false,
				isPrivate: false,
				isDeleted: false,
				isForbidden: false,
				selectedRevision: 0, // 0 means current revision
				latestKnownRevision: 0,
				diffContent: '',
				prevRoute: '',
				page: {},
				error: 0,
				editor: new Editor({
					// editable: false,
					extensions: [
						new Blockquote(), new BulletList(), new HardBreak(), new HorizontalRule(),
						new Heading({ levels: [1, 2, 3] }), new Bold(), new Code(), new Strike(),
						new Italic(), new Underline(), new Link(), new Table({ resizable: false }),
						new TableHeader(), new TableCell(), new TableRow(), new Image(),
						new ListItem(), new OrderedList(), new Blockquote(), new History()
					],
					content: `
						<p><em>Loading page...</em></p>
					`
				})
			}
		},
		props: {
			pageIdentifier: String,
			urlTitle: String
		},
		computed: {
			lastModDateString() {
				return dateformat(this.lastModDate, 'mmmm d, yyyy')
			},
			isHomepage() {
				return this.pageIdentifier === '__HOMEPAGE__'
			},
			isLoggedIn() {
				return this.$store.getters.isLoggedIn
			}
		},
		components: {
			PageSidebar, EditorContent, LoginPopover, Popover, DesktopMenuBar
		},
		mounted() {
			window.scrollTo(0, 0);
			this.editor.setOptions({ editable: false })
			this.getPage()
		},
		watch: {
			pageIdentifier() {
				window.scrollTo(0, 0);
				this.getPage()
			},
			isLoggedIn() {
				this.getPage()
			},
			isEditing() {
				this.editor.setOptions({ editable: this.isEditing })

				if (this.isEditing) {
					this.editor.focus()
				} else {
					this.$nextTick(() => {
						Array.from(document.querySelectorAll('.editor__content a')).forEach(elm => elm.addEventListener('click', this.clickedContentLink))
					})
				}
			}
		},
		methods: {
			async getPage() {
				const page = await PagesService.getPage(this.pageIdentifier)

				if (page.data.error) {
					this.error = page.data.error
					this.$refs.pageNotFoundPopover.showFromElement()
					return
				}

				this.gotPage(page)
			},
			gotPage(page) {
				this.selectedRevision = 0
				this.diffContent = ''
				this.isPrivate = page.data.private
				this.isDeleted = page.data.isDeleted
				this.isForbidden = page.data.forbidden
				this.guid = page.data.guid // pageIdentifier could be a shortid
				this.title = page.data.title || ''
				this.editor.setContent(page.data.pageHtml || '')
				this.lastModUser = page.data.lastMod ? page.data.lastMod.user : ''
				this.lastModDate = page.data.lastMod ? new Date(page.data.lastMod.date) : new Date()
				this.latestKnownRevision = page.data.revision
				this.tags.splice(0, this.tags.length)
				this.tags.push.apply(this.tags, page.data.tags || [])
				
				document.title = page.data.title || ''

				if (page.data.forbidden) {
					this.$nextTick(() => this.$refs.loginToView.showFromElement())
				}

				this.setupRouterLinks()
			},
			async toggleEdit() {
				if (!this.isEditing) {
					await this.getPage()
				}
				this.isEditing = !this.isEditing
			},
			cancelEdit() {
				this.isEditing = false
				this.getPage()
			},
			clickedContentLink(e) {
				if (this.isEditing) {
					return;
				}

				const href = e.target.getAttribute('href')
				
				if (href && href.match(/^\/wiki\/pages\/.+/)) {
					e.preventDefault()
					this.$router.push(e.target.getAttribute('href'))
				}
			},
			setupRouterLinks() {
				this.$nextTick(() => {
					Array.from(document.querySelectorAll('.editor__content a')).forEach(elm => elm.addEventListener('click', this.clickedContentLink))
				})
			},
			teardownRouterLinks() {
				this.$nextTick(() => {
					Array.from(document.querySelectorAll('.editor__content a')).forEach(elm => elm.removeEventListener('click', this.clickedContentLink))
				})
			},
			dataURItoFile(dataURI) {
				const filenameMaps = {
					'image/jpg': 'droppedFile.jpg',
					'image/jpeg': 'droppedFile.jpg',
					'image/png': 'droppedFile.png',
					'image/gif': 'droppedFile.gif'
				}
				const byteString = atob(dataURI.split(',')[1])
				const mimeType = dataURI.match(/^data:(image\/[^;]+)/)[1]
				const filename = filenameMaps[mimeType] || 'droppedFile.jpg'
				const ab = new ArrayBuffer(byteString.length)
				const ia = new Uint8Array(ab)
				for (let i = 0; i < byteString.length; i++) {
					ia[i] = byteString.charCodeAt(i)
				}
				return new File([ab], filename, { type: mimeType })
			},
			async saveEdit(shouldMakeRevision = true) {
				const updatedPageFromServer = await PagesService.getPage(this.pageIdentifier)

				if (updatedPageFromServer.data.revision > this.latestKnownRevision) {
					alert('This page cannot be saved, because it is out of date. Please cancel and re-apply your edits.')
					return
				}

				let pageTitle = this.$refs.title.innerText.trim()
				if (pageTitle.match(/\S/)) {
					this.title = pageTitle.trim()
				}

				let contentForServer = {
					title: this.title,
					content: this.editor.getHTML(),
					guid: this.guid,
					private: this.isPrivate,
					tags: this.tags,
					_shouldMakeRevision: shouldMakeRevision
				}

				// find all images on the page, look for images to upload
				const imageTestElement = document.createElement('div')
				imageTestElement.innerHTML = contentForServer.content

				const dataImageElements = Array.from(document.querySelectorAll('img'))
					.filter(imgElm => imgElm.src.indexOf('data:image/') === 0)

				if (dataImageElements.length > 0) {
					for (let imageIdx = 0; imageIdx < dataImageElements.length; imageIdx++) {
						let imageElm = dataImageElements[imageIdx]
						let aFile = this.dataURItoFile(imageElm.src)
						let uploadResponse = await PagesService.uploadAttachment(this.guid, aFile)

						contentForServer.content = contentForServer.content.replace(imageElm.src, uploadResponse.data.src)
					}
				}
				
				const updatedPage = await PagesService.updatePageContent(contentForServer)
				this.isEditing = false
				this.gotPage(updatedPage)
				this.$store.commit('setStatusMessage', 'Page saved.')
			},
			makePagePrivate() {
				this.isPrivate = true
				this.saveEdit(false)
			},
			makePagePublic() {
				this.isPrivate = false
				this.saveEdit(false)
			},
			iconStylesForButton(buttonName) {
				return {
					backgroundImage: `url(${icons[buttonName]})`
				}
			},
			tagsChanged(tags) {
				this.tags = tags
			},
			async handleSelectedRevision(rev) {

				this.selectedRevision = rev
				
				if (rev < 1) {
					this.getPage();
					return;
				}

				const revisionContent = await PagesService.getPageRevision(this.guid, rev)
				this.title = revisionContent.data.meta.title
				this.lastModDate = new Date(revisionContent.data.meta.lastMod.date)

				if (revisionContent.data.meta.lastMod.user) {
					this.lastModUser = revisionContent.data.meta.lastMod.user
				}

				if (revisionContent.data.diff) {
					// diffs are missing table wrappers; add them
					let wrappedDiffContent = revisionContent.data.diff

					let diffElm = document.createElement('div')
					diffElm.innerHTML = wrappedDiffContent

					let alreadyWrappedTables = Array.from(diffElm.querySelectorAll('.tableWrapper table'))

					Array.from(diffElm.querySelectorAll('table'))
						.filter(tableElm => alreadyWrappedTables.indexOf(tableElm) < 0) // not already wrapped
						.forEach(tableElm => {
							let wrapperDiv = document.createElement('div')
							wrapperDiv.className = 'tableWrapper'
							tableElm.parentNode.insertBefore(wrapperDiv, tableElm)
							tableElm.parentNode.removeChild(tableElm)
							wrapperDiv.appendChild(tableElm)
						})
					
					this.diffContent = diffElm.innerHTML
				} else {
					this.editor.setContent(revisionContent.data.content)
				}
			},
			async handleDeletePage() {
				const updatedPage = await PagesService.deletePage(this.guid)
				this.$store.commit('setStatusMessage', 'Page deleted.')
				this.gotPage(updatedPage)
			},
			async handleRestoreRevision() {
				const updatedPage = await PagesService.restorePage(this.guid, this.selectedRevision)
				this.$store.commit('setStatusMessage', 'Page restored.')
				this.gotPage(updatedPage)
			},
			clickedGotoHomepage() {
				this.$router.push('/')
			}
		},
		beforeRouteEnter(to, from, next) {
			next(vm => {
				vm.prevRoute = (typeof from === 'string' ? from : '')
			})
		}
	}

</script>


<style lang="scss">

	path {
		fill: white;
	}

	.is-viewing-diff .editor__content {
		display: none;
	}

	.page {

		position: relative;

		&.main-container {
			transition: background-color 0.5s;

			&.is-editing {
				background-color: #fff;

				.menu-bubble.is-active, .floating-menu.is-active {
					opacity: 1;
					transform: translateY(0);
					visibility: visible;
				}

				// .ProseMirror-focused:focus {
				// 	outline-width: 4px;
				// }
			}

			.viewing-revision {
				position: absolute;
				top: 0;
				right: 0;
				width: 100%;
				text-align: right;
				background-color: #771c44;
				color: #fff;
				
				&::after {
					display: inline-block;
					width: 3px;
					content: ' '
				}
			}

			.inline-last-mod {
				display: none;

				@media (max-height: 500px) {
					display: inline;
				}
			}

			.ProseMirror {
				outline: none;
			}

			.ProseMirror-focused:focus {
				outline: none;
			}
		}

		@include mobile {
			width: $mobile-page-width;
			// min-height: calc(110vh - #{$nav-height} - #{$footer-height} - 1px);
		}

		& > article {
			margin: 0 0 0 $sidebar-width;
			padding: 0 50px calc((100vw / #{$grass-ratio}) - 50px) 50px;

			@include mobile {
				margin-left: 0;
				padding: 0 30px calc((110vw / #{$grass-ratio})) 30px;
			}

			@include mobile-landscape {
				margin-left: 0;
				padding: 0 30px calc((110vw / #{$grass-ratio})) 30px;
			}

			.title {
				outline: none;
			}

			& > .last-mod-date {
				margin-top: 0;
				margin-bottom: 12px;
				font-size: 16px;
				line-height: 16px;
				text-align: center;
			}

			img {
				display: block;
				margin: 4px auto;
				max-width: 100%;
				border: 1px solid $page-border-color;
				box-shadow: 0 0 3px $page-border-color;
			}

			a.attachment {
				display: inline-block;
				border: 1px solid #aaa;
				border-radius: 6px;
				padding: 0 10px 2px 8px;
				margin: 0 3px;
				font-size: 0.9em;
				text-decoration: none;
				background-color: #fff;
				box-shadow: 0 0 2px #ccc;
				transition: box-shadow 0.5s;

				&:hover {
					text-decoration: none;
					box-shadow: 0 0 6px #bbb;
				}

				&::before {
					position: relative;
					top: 3px;
					left: 0;
					margin-right: 2px;
					font-family: 'Material Icons';
					content: 'file_download';
				}
			}

			ins {
				background-color: #0c623b;
				color: #fff;
				margin: auto 2px;
			}
			
			del {
				background-color: #771c44;
				color: #fff;
				margin: auto 2px;
			}
		}

		li p {
			margin: 2px 0;
		}

		.tableWrapper {
			overflow-x: auto;
		}

		hr {
			width: 98%;
			border-right: 2px solid $page-border-color;
		}

		table {
			border: 1px solid $page-border-color;
			box-shadow: 0 0 3px $page-border-color;
			border-spacing: 0;
			width: 98% !important;
			margin-left: auto;
			margin-right: auto;

			col {
				width: auto !important;
			}

			tr:nth-child(even) {
				background: #eeeee5;

				td {
					border-right-color: $body-background-color;
				}
			}

			td, th {
				margin: 0;
				padding: 4px;
				min-width: 50px;
				border-right: 1px solid #eeeee5;

				&:last-child {
					border-right-width: 0;
				}
			}

			td {
				vertical-align: top;
			}

			h1, h2, h3, h4, h5, h6, p {
				margin: 0;
				padding: 0 0 10px 0;
			}
		}

		.popover.page-not-found-popover.error form {
			min-width: 260px;

			.buttons button:first-child {
				display: none;
			}
		}
	}

</style>
